/**
 * Interior Explorer — Vanilla JS (no Alpine.js dependency)
 *
 * A fullscreen interactive interior kit explorer with:
 * - Kitchen & bathroom scene switching
 * - Hotspot navigation linked to product list
 * - IntersectionObserver-driven active item highlighting
 * - Finish/countertop toggle (Matte Black / Brushed Nickel, countertop styles)
 * - Trigger card with background image slideshow
 */

(function () {
  'use strict';

  // ── Utility ──────────────────────────────────────────────────────────
  const $ = (sel, ctx) => (ctx || document).querySelector(sel);
  const $$ = (sel, ctx) => [...(ctx || document).querySelectorAll(sel)];

  // ── InteriorExplorerTrigger ──────────────────────────────────────────
  class InteriorExplorerTrigger {
    constructor(el) {
      this.el = el;
      this.images = JSON.parse(el.dataset.slideshowImages || '[]');
      this.activeIndex = 0;
      this.intervalId = null;
      this.backgrounds = $$('[data-slideshow-bg]', el);

      this.el.addEventListener('click', () => {
        window.dispatchEvent(new CustomEvent('open-interior-explorer', {
          detail: { sectionId: el.closest('[data-interior-explorer]')?.id }
        }));
      });

      if (this.images.length > 0) this.startSlideshow();
    }

    startSlideshow() {
      this.render();
      this.intervalId = setInterval(() => {
        this.activeIndex = (this.activeIndex + 1) % this.images.length;
        this.render();
      }, 4800);
    }

    render() {
      this.backgrounds.forEach((bg, i) => {
        bg.style.backgroundImage = `url('${this.images[i]}')`;
        if (i === this.activeIndex) {
          bg.classList.add('active');
        } else {
          bg.classList.remove('active');
        }
      });
    }

    destroy() {
      if (this.intervalId) clearInterval(this.intervalId);
    }
  }

  // ── InteriorExplorer (main modal) ────────────────────────────────────
  class InteriorExplorer {
    constructor(el) {
      this.el = el;
      this.scenes = JSON.parse($('[data-explorer-scenes]', el)?.textContent || '{}');
      this.currentSceneKey = 'kitchen';
      this.currentFinish = 'Matte Black';
      this.currentCountertop = 'Silverstone';
      this.activeItemId = null;
      this.isOpen = false;
      this.observers = [];

      // Elements
      this.modal = $('[data-explorer-modal]', el);
      this.sceneImage = $('[data-scene-image]', el);
      this.loadingIndicator = $('[data-scene-loading]', el);
      this.hotspotsContainer = $('[data-hotspots]', el);
      this.productList = $('[data-product-list]', el);
      this.scrollContainer = $('[data-scroll-container]', el);

      // Create backdrop
      this.backdrop = document.createElement('div');
      this.backdrop.className = 'ie-modal-backdrop';
      this.backdrop.addEventListener('click', () => this.close());
      document.body.appendChild(this.backdrop);

      this.bindEvents();
    }

    get sceneData() {
      return this.scenes[this.currentSceneKey] || { backgroundImage: '', items: [] };
    }

    get productItems() {
      return this.sceneData.items.filter(item => item.type !== 'navigation');
    }

    get navigationItems() {
      return this.sceneData.items.filter(item => item.type === 'navigation');
    }

    bindEvents() {
      // Open from trigger
      window.addEventListener('open-interior-explorer', (e) => {
        const targetId = e.detail?.sectionId;
        if (!targetId || targetId === this.el.id) this.open();
      });

      // Close button
      $$('[data-explorer-close]', this.el).forEach(btn => {
        btn.addEventListener('click', () => this.close());
      });

      // Close on backdrop click
      this.modal?.addEventListener('click', (e) => {
        if (e.target === this.modal) this.close();
      });

      // ESC to close
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.isOpen) this.close();
      });
    }

    open() {
      this.isOpen = true;
      this.activeItemId = null;
      this.modal.classList.add('open');
      document.body.classList.add('ie-modal-active');
      document.body.style.overflow = 'hidden';
      if (this.backdrop) this.backdrop.classList.add('open');
      this.renderScene();
      setTimeout(() => this.setupIntersectionObservers(), 150);
    }

    close() {
      this.isOpen = false;
      this.modal.classList.remove('open');
      document.body.classList.remove('ie-modal-active');
      document.body.style.overflow = '';
      if (this.backdrop) this.backdrop.classList.remove('open');
      this.cleanupObservers();
    }

    switchScene(sceneKey) {
      if (!this.scenes[sceneKey]) return;
      this.cleanupObservers();
      this.currentSceneKey = sceneKey;
      this.activeItemId = null;

      // Show loading
      if (this.loadingIndicator) this.loadingIndicator.style.display = '';
      if (this.sceneImage) this.sceneImage.style.opacity = '0';

      this.renderScene();

      if (this.scrollContainer) this.scrollContainer.scrollTop = 0;
      setTimeout(() => this.setupIntersectionObservers(), 150);
    }

    renderScene() {
      const data = this.sceneData;

      // Update scene image
      if (this.sceneImage) {
        this.sceneImage.src = data.backgroundImage;
        this.sceneImage.onload = () => {
          this.sceneImage.style.opacity = '1';
          if (this.loadingIndicator) this.loadingIndicator.style.display = 'none';
        };
      }

      // Render hotspots
      if (this.hotspotsContainer) {
        this.hotspotsContainer.innerHTML = data.items.map(item => this.renderHotspot(item)).join('');
        $$('[data-hotspot]', this.hotspotsContainer).forEach(btn => {
          btn.addEventListener('click', () => {
            const id = btn.dataset.hotspot;
            const item = data.items.find(i => i.id === id);
            if (item) this.handleHotspotClick(item);
          });
        });
      }

      // Render product list
      if (this.productList) {
        this.productList.innerHTML =
          this.productItems.map(item => this.renderProductCard(item)).join('') +
          this.navigationItems.map(item => this.renderNavLink(item)).join('');

        // Bind finish toggles
        $$('[data-finish-btn]', this.productList).forEach(btn => {
          btn.addEventListener('click', () => {
            this.currentFinish = btn.dataset.finishBtn;
            this.updateFinishUI();
          });
        });

        // Bind countertop toggles
        $$('[data-countertop-btn]', this.productList).forEach(btn => {
          btn.addEventListener('click', () => {
            this.currentCountertop = btn.dataset.countertopBtn;
            this.updateCountertopUI();
          });
        });

        // Bind nav links
        $$('[data-switch-scene]', this.productList).forEach(btn => {
          btn.addEventListener('click', () => this.switchScene(btn.dataset.switchScene));
        });
      }
    }

    renderHotspot(item) {
      if (item.type === 'navigation') {
        return `
          <button data-hotspot="${item.id}" style="left:${item.x}%;top:${item.y}%">
            <div class="ie-hotspot-nav">
              <div class="ie-hotspot-nav-icon">
                <svg style="width:0.625rem;height:0.625rem;" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"></path></svg>
              </div>
              <span>${item.title}</span>
            </div>
          </button>`;
      }

      return `
        <button data-hotspot="${item.id}" style="left:${item.x}%;top:${item.y}%" aria-label="Scroll to ${item.title}">
          <div style="position:relative;">
            <span data-hotspot-ring="${item.id}" class="ie-hotspot-ring"></span>
            <div data-hotspot-dot="${item.id}" class="ie-hotspot-dot">
              <div data-hotspot-inner="${item.id}" class="ie-hotspot-inner"></div>
            </div>
            <div data-hotspot-label="${item.id}" class="ie-hotspot-label">${item.title}</div>
          </div>
        </button>`;
    }

    renderProductCard(item) {
      const imageSrc = item.images?.[this.currentFinish] || item.images?.['Standard'] || '';

      let togglesHtml = '';
      if (item.id === 'faucet') {
        togglesHtml = this.renderFinishToggles();
      }
      if (item.title && item.title.includes('Countertop')) {
        togglesHtml = this.renderCountertopToggles();
      }

      return `
        <div id="product-${item.id}" data-product-item="${item.id}">
          <div class="ie-product-mobile-img">
            <img data-product-image="${item.id}" src="${imageSrc}" alt="${item.title}" style="width:100%;height:100%;object-fit:cover;" />
          </div>
          <div>
            <div class="ie-product-subtitle">${item.subtitle || ''}</div>
            <h3 data-product-title="${item.id}">${item.title}</h3>
            <p class="ie-product-desc">${item.description || ''}</p>
            ${togglesHtml}
            <div style="display:flex;flex-direction:column;gap:0.75rem;align-items:flex-start;">
              <div style="width:100%;">
                <ul class="ie-spec-list">
                  ${(item.specs || []).map(spec => `
                    <li>
                      <svg class="ie-spec-check" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path></svg>
                      <span>${spec}</span>
                    </li>
                  `).join('')}
                </ul>
              </div>
              <div class="ie-product-desktop-img">
                <img data-product-image-lg="${item.id}" src="${imageSrc}" alt="${item.title}" class="ie-product-img" />
              </div>
            </div>
          </div>
        </div>`;
    }

    renderFinishToggles() {
      const finishes = ['Matte Black', 'Brushed Nickel'];
      return `
        <div style="display:flex;justify-content:center;">
          <div class="ie-toggle-group">
            ${finishes.map(f => `
              <button data-finish-btn="${f}" class="ie-toggle-btn ${f === this.currentFinish ? 'active' : ''}">${f}</button>
            `).join('')}
          </div>
        </div>`;
    }

    renderCountertopToggles() {
      const types = ['Silverstone', 'Merino Grey', 'Yuri Grey'];
      return `
        <div style="display:flex;justify-content:center;">
          <div class="ie-toggle-group">
            ${types.map(c => `
              <button data-countertop-btn="${c}" class="ie-toggle-btn ${c === this.currentCountertop ? 'active' : ''}">${c}</button>
            `).join('')}
          </div>
        </div>`;
    }

    renderNavLink(item) {
      return `
        <div class="ie-nav-link">
          <button data-switch-scene="${item.targetScene}">
            <span>${item.title}</span>
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 8l4 4m0 0l-4 4m4-4H3"></path></svg>
          </button>
        </div>`;
    }

    updateFinishUI() {
      const data = this.sceneData;
      $$('[data-finish-btn]', this.productList).forEach(btn => {
        btn.classList.toggle('active', btn.dataset.finishBtn === this.currentFinish);
      });

      // Update product images for items with finish variants
      data.items.forEach(item => {
        if (!item.images?.[this.currentFinish]) return;
        const src = item.images[this.currentFinish];
        $$(`[data-product-image="${item.id}"], [data-product-image-lg="${item.id}"]`, this.productList).forEach(img => {
          img.src = src;
        });
      });
    }

    updateCountertopUI() {
      $$('[data-countertop-btn]', this.productList).forEach(btn => {
        btn.classList.toggle('active', btn.dataset.countertopBtn === this.currentCountertop);
      });
    }

    handleHotspotClick(item) {
      if (item.type === 'navigation') {
        this.switchScene(item.targetScene);
        return;
      }
      this.setActiveItem(item.id);
      const el = document.getElementById(`product-${item.id}`);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }

    setActiveItem(id) {
      if (this.activeItemId === id) return;
      this.activeItemId = id;

      // Update product cards
      $$('[data-product-item]', this.productList).forEach(card => {
        if (card.dataset.productItem === id) {
          card.classList.add('active');
        } else {
          card.classList.remove('active');
        }
      });

      // Update hotspots
      $$('[data-hotspot]', this.hotspotsContainer).forEach(btn => {
        const hid = btn.dataset.hotspot;
        const ring = $(`[data-hotspot-ring="${hid}"]`, btn);
        const dot = $(`[data-hotspot-dot="${hid}"]`, btn);
        const inner = $(`[data-hotspot-inner="${hid}"]`, btn);
        const label = $(`[data-hotspot-label="${hid}"]`, btn);
        const isActive = hid === id;

        btn.style.transform = `translate(-50%, -50%) scale(${isActive ? 1.25 : 1})`;
        if (ring) ring.classList.toggle('active', isActive);
        if (dot) dot.classList.toggle('active', isActive);
        if (inner) inner.classList.toggle('active', isActive);
        if (label) label.classList.toggle('active', isActive);
      });
    }

    setupIntersectionObservers() {
      this.cleanupObservers();
      this.productItems.forEach(item => {
        const el = document.getElementById(`product-${item.id}`);
        if (!el) return;
        const observer = new IntersectionObserver(
          entries => entries.forEach(entry => { if (entry.isIntersecting) this.setActiveItem(item.id); }),
          { root: null, rootMargin: '-40% 0px -40% 0px', threshold: 0 }
        );
        observer.observe(el);
        this.observers.push({ observer, el });
      });
    }

    cleanupObservers() {
      this.observers.forEach(({ observer, el }) => { observer.unobserve(el); observer.disconnect(); });
      this.observers = [];
    }
  }

  // ── Initialize ──────────────────────────────────────────────────────
  function init() {
    $$('[data-explorer-trigger]').forEach(el => new InteriorExplorerTrigger(el));
    $$('[data-interior-explorer]').forEach(el => new InteriorExplorer(el));
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Global helper
  window.openInteriorExplorer = function (sectionId) {
    window.dispatchEvent(new CustomEvent('open-interior-explorer', { detail: { sectionId } }));
  };
})();
